
"use client";

import React from 'react';
import { usePathname } from 'next/navigation';
import { Toaster } from "@/components/ui/toaster";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { useKioskMode } from "@/contexts/KioskModeContext";
import { cn } from "@/lib/utils";
import { Loader2 } from 'lucide-react';

export default function AppLayoutClient({ children }: { children: React.ReactNode }) {
  const { isKioskMode } = useKioskMode();
  const { firebaseUser, isLoadingAuth, isLoadingSettings, currentStoreId } = useAuth();
  const pathname = usePathname();

  // Define paths that should never show the main header.
  const noHeaderPaths = ['/', '/signup', '/login-pin', '/create-store'];
  
  const showHeader = !isLoadingAuth && !isKioskMode && !noHeaderPaths.includes(pathname);

  // Define paths that have a header but should not be constrained by the container.
  const fullWidthHeaderPaths = ['/order', '/floor-plan', '/menu-management', '/kds'];
  const useContainer = showHeader && !fullWidthHeaderPaths.includes(pathname);

  // Define paths that are critically dependent on storeId and shopSettings being loaded.
  const settingsDependentPaths = ['/order', '/kiosk', '/dashboard/receipt-editor', '/kds', '/shop-info', '/menu-management', '/floor-plan', '/cash-up', '/driver-reconciliation', '/users', '/reports'];
  
  const needsSettingsCheck = settingsDependentPaths.some(p => pathname.startsWith(p));
  
  // If we are on a settings-dependent page, we must wait for auth and settings to be loaded.
  if (needsSettingsCheck && (isLoadingAuth || isLoadingSettings)) {
    return (
       <div className="flex h-screen w-screen items-center justify-center bg-secondary">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4 text-lg text-muted-foreground">Initializing Application...</p>
      </div>
    );
  }

  return (
    <>
      {showHeader && <Header />}
      <main className={cn(
        "flex-grow",
        useContainer && "container mx-auto p-4 md:p-6 lg:p-8"
      )}>
        {children}
      </main>
      <Toaster />
    </>
  );
}
